'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useCart } from '@/context/CartContext';
import {
  X,
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  ArrowRight,
  Store,
  ShieldCheck,
  Tag,
  Loader2,
  CheckCircle2,
  Package
} from 'lucide-react';

export function CartDrawer() {
  const {
    cart,
    itemCount,
    isDrawerOpen,
    closeDrawer,
    updateQuantity,
    removeItem,
    applyCoupon
  } = useCart();

  const [couponInput, setCouponInput] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  if (!isDrawerOpen) return null;

  const handleApplyCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!couponInput.trim()) return;
    setApplyingCoupon(true);
    await applyCoupon(couponInput.trim());
    setApplyingCoupon(false);
  };

  const currencySymbol = cart?.currency === 'GH' || cart?.currency === 'GHS' ? 'GH₵' : (cart?.currency === 'KES' ? 'KSh' : '₦');
  const formatMoney = (minor: number) => {
    return `${currencySymbol} ${(minor / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden font-sans">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs transition-opacity duration-300"
        onClick={closeDrawer}
      />

      <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
        <div className="w-screen max-w-md bg-white dark:bg-[#070D1E] shadow-2xl flex flex-col justify-between border-l border-slate-200 dark:border-slate-800">
          
          {/* Header */}
          <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800/80 flex items-center justify-between bg-slate-50/50 dark:bg-[#0A122C]/50">
            <div className="flex items-center gap-2.5">
              <div className="size-9 rounded-xl bg-[#0066FF]/10 text-[#0066FF] flex items-center justify-center">
                <ShoppingCart className="size-4.5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  Shopping Cart
                  {itemCount > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#0066FF] text-white font-bold">
                      {itemCount}
                    </span>
                  )}
                </h2>
                <p className="text-[11px] text-slate-500">Multi-Vendor African Automotive Hub</p>
              </div>
            </div>

            <button
              type="button"
              onClick={closeDrawer}
              className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X className="size-5" />
            </button>
          </div>

          {/* Cart Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-6">
            {!cart || cart.vendorGroups.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 space-y-4">
                <div className="size-16 rounded-3xl bg-slate-100 dark:bg-slate-800/60 flex items-center justify-center text-slate-400">
                  <Package className="size-8 stroke-[1.5]" />
                </div>
                <div className="space-y-1 max-w-xs">
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">Your cart is empty</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Search parts by make, model, year, or VIN to find verified spare parts with guaranteed fitment.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeDrawer}
                  className="px-4 py-2 bg-[#0066FF] hover:bg-[#0052CC] text-white text-xs font-bold rounded-xl transition-all shadow-xs cursor-pointer"
                >
                  Explore Catalog
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Multi-Vendor Groups */}
                {cart.vendorGroups.map((group, groupIdx) => (
                  <div
                    key={group.sellerId || groupIdx}
                    className="border border-slate-200/80 dark:border-slate-800 rounded-2xl p-3.5 bg-white dark:bg-[#0B1536] shadow-2xs space-y-3"
                  >
                    {/* Vendor Header */}
                    <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-slate-800/60 text-xs">
                      <div className="flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-200 truncate">
                        <Store className="size-3.5 text-[#0066FF] shrink-0" />
                        <span className="truncate">{group.sellerName}</span>
                      </div>
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800">
                        {group.hubSlug.replace('-', ' ')}
                      </span>
                    </div>

                    {/* Vendor Items */}
                    <div className="space-y-3">
                      {group.items.map((item) => (
                        <div key={item.id} className="flex gap-3 items-center">
                          <img
                            src={item.imageUrl || '/images/parts/placeholder-part.png'}
                            alt={item.title}
                            className="size-14 rounded-xl object-cover border border-slate-200 dark:border-slate-700 shrink-0 bg-slate-50 dark:bg-slate-800"
                            onError={(e: any) => { e.target.src = '/images/parts/placeholder-part.png'; }}
                          />

                          <div className="flex-1 min-w-0 space-y-1">
                            <h4 className="text-xs font-bold text-slate-900 dark:text-white truncate">
                              {item.title}
                            </h4>
                            {item.sku && (
                              <span className="text-[10px] font-mono text-slate-400 block truncate">
                                SKU: {item.sku}
                              </span>
                            )}
                            <div className="text-xs font-bold text-[#0066FF]">
                              {formatMoney(item.unitPriceMinor)}
                            </div>
                          </div>

                          {/* Stepper & Delete */}
                          <div className="flex flex-col items-end gap-1.5 shrink-0">
                            <button
                              type="button"
                              onClick={() => removeItem(item.id)}
                              className="text-slate-400 hover:text-red-500 transition-colors p-1"
                              title="Remove item"
                            >
                              <Trash2 className="size-3.5" />
                            </button>

                            <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-slate-50 dark:bg-slate-800 text-xs">
                              <button
                                type="button"
                                onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                                className="px-1.5 py-1 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                              >
                                <Minus className="size-3" />
                              </button>
                              <span className="px-2 py-0.5 font-bold text-slate-800 dark:text-slate-200 min-w-[20px] text-center">
                                {item.quantity}
                              </span>
                              <button
                                type="button"
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                className="px-1.5 py-1 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                              >
                                <Plus className="size-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Vendor Sub-Dispatch */}
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800/60 flex items-center justify-between text-[11px] text-slate-500">
                      <span>Dispatch from {group.hubSlug.replace('-', ' ')}</span>
                      <span className="font-semibold text-slate-700 dark:text-slate-300">
                        {formatMoney(group.vendorShippingMinor)}
                      </span>
                    </div>
                  </div>
                ))}

                {/* Coupon Code Section */}
                <form onSubmit={handleApplyCoupon} className="space-y-2 pt-2">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag className="size-3.5 absolute left-3 top-3 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Promo code (e.g. P4D-FIRSTORDER)"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value)}
                        className="w-full pl-8 pr-3 py-2 text-xs uppercase font-mono rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#070D1E] text-slate-900 dark:text-white"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={applyingCoupon || !couponInput.trim()}
                      className="px-3 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-xl text-xs font-bold hover:bg-slate-800 transition-colors disabled:opacity-50 shrink-0 cursor-pointer"
                    >
                      {applyingCoupon ? <Loader2 className="size-3.5 animate-spin" /> : 'Apply'}
                    </button>
                  </div>

                  {cart.couponCode && (
                    <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                      <CheckCircle2 className="size-3.5" />
                      <span>Coupon <strong>{cart.couponCode}</strong> applied ({formatMoney(cart.discountMinor)} off)</span>
                    </div>
                  )}
                </form>
              </div>
            )}
          </div>

          {/* Footer Checkout Bar */}
          {cart && cart.vendorGroups.length > 0 && (
            <div className="p-4 sm:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0A122C]/50 space-y-4">
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between text-slate-500">
                  <span>Parts Subtotal</span>
                  <span className="font-semibold text-slate-900 dark:text-white">{formatMoney(cart.subtotalMinor)}</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>Logistics &amp; Hub Dispatch</span>
                  <span className="font-semibold text-slate-900 dark:text-white">{formatMoney(cart.shippingTotalMinor)}</span>
                </div>
                {cart.discountMinor > 0 && (
                  <div className="flex justify-between text-emerald-600 font-medium">
                    <span>Discount</span>
                    <span>-{formatMoney(cart.discountMinor)}</span>
                  </div>
                )}
                <div className="pt-2 border-t border-slate-200 dark:border-slate-800 flex justify-between items-baseline">
                  <span className="text-sm font-bold text-slate-900 dark:text-white">Total</span>
                  <span className="text-lg font-black text-[#0066FF] font-mono">
                    {formatMoney(cart.totalMinor)}
                  </span>
                </div>
              </div>

              <Link
                href="/checkout"
                onClick={closeDrawer}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#0066FF] hover:bg-[#0052CC] text-white font-bold rounded-2xl text-xs transition-all shadow-md shadow-[#0066FF]/20 cursor-pointer"
              >
                <span>Proceed to Secure Checkout</span>
                <ArrowRight className="size-4" />
              </Link>

              <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
                <ShieldCheck className="size-3.5 text-emerald-600" />
                <span>100% Fitment Guaranteed • Escrow Protection</span>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
